// Why an unplanned visit was started (D-182).
//
// A fixed set, the same for every organization in this release: the code is
// what travels and what the server persists, and the text a person reads is
// the catalog's, so the device never shows a code (D-179). `other` carries
// the operator's own words beside the code.
//
// The document's `unplanned_reason` is a free string the API bounds at 200
// characters, so the code travels inside it: the code alone for the four
// fixed reasons, and the code followed by the operator's text for `other`.
// The device validates that text against the same bound before it saves, so
// nothing the operator typed is lost to a rejection later (criterion 59).
import type { UnplannedReasonCode } from '../db/database';

/** The API's bound on `VisitSyncItem.unplanned_reason` (app.schemas.visit). */
export const MAX_UNPLANNED_REASON = 200;

/** What separates the code from the operator's words in an `other` reason. */
export const OTHER_PREFIX = 'other: ';

/** How much the operator may type under `other`, so the whole reason fits. */
export const MAX_OTHER_TEXT = MAX_UNPLANNED_REASON - OTHER_PREFIX.length;

/** The reason as the document carries it: the code, and the words of `other`. */
export function unplannedReason(code: UnplannedReasonCode, text: string): string {
  if (code !== 'other') return code;
  return `${OTHER_PREFIX}${text.trim()}`;
}

/** The code inside a stored reason, for a document written before this release
 *  or by another path. Null when the reason carries none. */
export function reasonCodeOf(reason: string | null): UnplannedReasonCode | null {
  if (reason?.startsWith(OTHER_PREFIX) === true) return 'other';
  if (reason === null) return null;
  switch (reason) {
    case 'claim':
    case 'incident':
    case 'follow_up':
    case 'extra':
    case 'other':
      return reason;
    default:
      return null;
  }
}

/** The operator's own words inside an `other` reason, or the empty string. */
export function reasonTextOf(reason: string | null): string {
  if (reason?.startsWith(OTHER_PREFIX) !== true) return '';
  return reason.slice(OTHER_PREFIX.length);
}

/** Whether the reason can be saved: a code of the set, and, under `other`,
 *  words that are neither empty nor longer than the schema allows. */
export function reasonIsValid(code: UnplannedReasonCode, text: string): boolean {
  if (code !== 'other') return true;
  const trimmed = text.trim();
  return trimmed !== '' && trimmed.length <= MAX_OTHER_TEXT;
}
